import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { Dialog } from "./Dialog.js";
import { Drawer } from "./Drawer.js";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMediaQuery } from "../hooks/index.js";

// Context to share media query result across all sub-components
const DrawerOrDialogContext = React.createContext<boolean | null>(null);

const useIsDesktop = () => {
  const context = React.useContext(DrawerOrDialogContext);
  // Fallback to hook if used outside of Root (shouldn't happen, but safe)
  const fallback = useMediaQuery("(min-width: 768px)");
  return context ?? fallback;
};

const Root = ({
  children,
  drawer: { shouldScaleBackground = true, ...props },
  dialog: { ...dialogProps },
  nested = false
}: {
  children: React.ReactNode;
  drawer: React.ComponentProps<typeof DrawerPrimitive.Root>;
  dialog: React.ComponentProps<typeof DialogPrimitive.Root>;
  nested?: boolean;
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const content = isDesktop ? (
    <DialogPrimitive.Root {...dialogProps}>{children}</DialogPrimitive.Root>
  ) : (
    (() => {
      const DrawerRoot = nested
        ? DrawerPrimitive.NestedRoot
        : DrawerPrimitive.Root;
      return (
        <DrawerRoot shouldScaleBackground={shouldScaleBackground} {...props}>
          {children}
        </DrawerRoot>
      );
    })()
  );

  return (
    <DrawerOrDialogContext.Provider value={isDesktop}>
      {content}
    </DrawerOrDialogContext.Provider>
  );
};

Root.displayName = "DrawerOrDialogRoot";

const Trigger = ({
  children,
  asChild,
  ref
}: {
  children: React.ReactNode;
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return (
      <DialogPrimitive.Trigger ref={ref} asChild={asChild}>
        {children}
      </DialogPrimitive.Trigger>
    );
  }
  return (
    <DrawerPrimitive.Trigger ref={ref} asChild={asChild}>
      {children}
    </DrawerPrimitive.Trigger>
  );
};

Trigger.displayName = "DrawerOrDialogTrigger";

const Content = ({
  children,
  position = "center",
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  innerClassName?: string;
  position?: "center" | "top" | "bottom";
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return (
      <Dialog.Content {...props} ref={ref} position={position}>
        {children}
      </Dialog.Content>
    );
  }
  return (
    <Drawer.Content ref={ref} {...props}>
      {children}
    </Drawer.Content>
  );
};

Content.displayName = "DrawerOrDialogContent";

const Title = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>) => {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return <Dialog.Title {...props}>{children}</Dialog.Title>;
  }
  return <Drawer.Title {...props}>{children}</Drawer.Title>;
};

Title.displayName = "DrawerOrDialogTitle";

const DrawerOrDialog = {
  Root,
  Trigger,
  Content,
  Title
};

export { DrawerOrDialog };
